import { useRef, useEffect, useState } from "react";
import { segmentBackground, initializeSegmenter } from "@/utils/backgroundSegmentation";

interface UseVirtualBackgroundProps {
  videoStream: MediaStream | null;
  background: 'none' | 'blur' | string;
}

export const useVirtualBackground = ({ videoStream, background }: UseVirtualBackgroundProps) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [processedStream, setProcessedStream] = useState<MediaStream | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const animationFrameRef = useRef<number>();

  useEffect(() => {
    if (!videoStream || background === 'none') {
      setProcessedStream(videoStream);
      return;
    }

    const initializeBackground = async () => {
      try {
        // Initialize segmenter
        await initializeSegmenter();
        setIsInitialized(true);
      } catch (error) {
        console.error('Failed to initialize background processing:', error);
        setProcessedStream(videoStream);
      }
    };

    initializeBackground();
  }, [videoStream, background]);

  useEffect(() => {
    if (!videoStream || background === 'none' || !isInitialized) {
      setProcessedStream(videoStream);
      return;
    }

    const videoTrack = videoStream.getVideoTracks()[0];
    if (!videoTrack) {
      setProcessedStream(videoStream);
      return;
    }

    // Create canvas for processing
    const canvas = document.createElement('canvas');
    canvasRef.current = canvas;

    // Create video element for source
    const video = document.createElement('video');
    video.autoplay = true;
    video.muted = true;
    video.playsInline = true;
    video.srcObject = videoStream;
    videoRef.current = video;

    const processFrame = async () => {
      if (!canvas || !video || video.readyState !== video.HAVE_ENOUGH_DATA) {
        animationFrameRef.current = requestAnimationFrame(processFrame);
        return;
      }

      try {
        // Process all backgrounds except 'none' - includes blur, URLs, and local paths
        if (background !== 'none') {
          await segmentBackground(video, canvas, background as 'blur' | string);
        } else {
          // No background effect - just draw video
          const ctx = canvas.getContext('2d');
          if (ctx) {
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          }
        }
      } catch (error) {
        console.error('Frame processing error:', error);
      }

      animationFrameRef.current = requestAnimationFrame(processFrame);
    };

    video.onloadedmetadata = () => {
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      
      // Start processing
      processFrame();

      // Create stream from canvas
      const stream = canvas.captureStream(30);
      const audioTracks = videoStream.getAudioTracks();
      audioTracks.forEach(track => stream.addTrack(track));
      setProcessedStream(stream);
    };

    video.play().catch(err => console.error('Video play error:', err));

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (video) {
        video.pause();
        video.srcObject = null;
      }
    };
  }, [videoStream, background, isInitialized]);

  return { processedStream };
};
